'use client';
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 flex flex-col items-center justify-center">
        <section className="w-full py-20 px-4 flex flex-col items-center bg-gradient-to-b from-neutral-100/60 to-background dark:from-slate-900/80 dark:to-background transition-colors">
          <div className="max-w-3xl w-full">
            <Card className="w-full rounded-2xl shadow-2xl border-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg p-0">
              <div className="flex flex-col items-center gap-2 pt-10">
                <div className="bg-sky-100 dark:bg-sky-900 rounded-full p-4 mb-2">
                  <ShieldCheck className="h-8 w-8 text-sky-700 dark:text-sky-400" />
                </div>
                <h1 className="text-4xl font-extrabold text-sky-700 dark:text-sky-400 text-center tracking-tight mb-2">
                  Privacy Policy
                </h1>
                <p className="text-base text-muted-foreground text-center max-w-xl mb-6">
                  Your privacy matters. Below you&#39;ll find how we handle your personal data when using our services.
                </p>
              </div>
              <div className="px-8 pb-10 pt-2 space-y-10">
                {/* Definitions */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">Definitions</h2>
                  <ul className="list-disc pl-6 space-y-1 text-base">
                    <li><span className="font-medium text-foreground">Privacy Policy:</span> this document.</li>
                    <li><span className="font-medium text-foreground">Data Subject:</span> any natural person whose personal data is processed by the Controller.</li>
                    <li><span className="font-medium text-foreground">Controller:</span> Dutch Sim Pilot.</li>
                    <li><span className="font-medium text-foreground">Website:</span> <a href="https://www.flightdeck.me" className="underline text-sky-700 dark:text-sky-400">https://www.flightdeck.me</a>.</li>
                  </ul>
                </section>

                {/* 1. General */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">1. General</h2>
                  <p>
                    This Privacy Policy explains how Dutch Sim Pilot (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) processes personal data when you use the Website. We comply with the General Data Protection Regulation (GDPR) and other applicable privacy laws.
                  </p>
                </section>

                {/* 2. Personal Data */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">2. Personal Data We Process</h2>
                  <div className="bg-sky-50 dark:bg-slate-800 rounded-lg p-4 mb-3 border border-sky-100 dark:border-sky-800">
                    <p className="mb-2">
                      We deliberately keep data collection to a minimum. <b>We do <i>not</i> process or store user passwords.</b>
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>
                        <span className="font-medium">Name (first and last):</span> Used for user identification and legacy login.
                      </li>
                      <li>
                        <span className="font-medium">E-mail address:</span> Used for account verification, legacy login, and system notifications.
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Data is stored in plain text within a secured database. <b>All connections to the database are encrypted (TLS 1.3+).</b>
                    </p>
                  </div>
                  <p>
                    We may temporarily process the following technical data, which is <b>not</b> directly linked to your identity and is deleted or anonymised after the session ends:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>IP address</li>
                    <li>Device type and browser information</li>
                    <li>Basic functional cookies (see §6)</li>
                  </ul>
                </section>

                {/* 3. Source of Data */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">3. Source of Data: VATSIM SSO</h2>
                  <p>
                    Authentication is handled exclusively via the <b>VATSIM Single Sign-On (SSO)</b> service. VATSIM acts as the primary data controller for your account details. Upon successful login, VATSIM sends us a one-time verification payload containing your name and e-mail address. We store this data only to:
                  </p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>Facilitate current and future logins (including a fallback “legacy login” if the VATSIM SSO service is unavailable).</li>
                    <li>Provide basic Website functionality tied to your account.</li>
                  </ol>
                  <p>
                    We do <b>not</b> share this information with VATSIM after the initial exchange, nor with any other party.
                  </p>
                </section>

                {/* 4. Legal Bases */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">4. Legal Bases for Processing</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><b>Performance of a contract</b> (Art. 6 (1)(b) GDPR): to provide you with an account and core Website features.</li>
                    <li><b>Legitimate interests</b> (Art. 6 (1)(f) GDPR): to secure our platform and ensure continuity of service.</li>
                  </ul>
                </section>

                {/* 5. Minors */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">5. Minors</h2>
                  <p>
                    If you are under 16, you must obtain your parent’s or guardian’s consent before creating an account.
                  </p>
                </section>

                {/* 6. Cookies */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">6. Cookies or Similar Technologies</h2>
                  <p>
                    We use only strictly <b>functional cookies</b> that are necessary for the Website to operate (e.g. maintaining your login session). No marketing or profiling cookies are set, and no cookie data is shared with third parties. You can disable cookies in your browser, but the Website may not function correctly.
                  </p>
                </section>

                {/* 7. Parties With Access */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">7. Parties With Access to Personal Data</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><b>Internal personnel:</b> Access is restricted to authorised staff who need the data to perform their duties and who are bound by confidentiality.</li>
                    <li><b>No external processors:</b> We do not transfer your personal data to third parties.</li>
                  </ul>
                </section>

                {/* 8. Security Measures */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">8. Security Measures</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>All communication between your browser, our servers, and the database is encrypted using TLS 1.3 or higher.</li>
                    <li>The database resides in a secured private network with firewall rules and access controls.</li>
                    <li>Regular security patches and access-log reviews are performed.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">
                    <b>Note:</b> Data is not encrypted at rest. We minimise risk through strict access controls and encryption in transit.
                  </p>
                </section>

                {/* 9. Retention Period */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">9. Retention Period</h2>
                  <p>
                    We retain your name and e-mail address <b>until you delete your account</b> or two weeks after the termination of our services to you—whichever comes first—unless a longer retention period is required by law.
                  </p>
                </section>

                {/* 10. Your Rights */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">10. Your Rights</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Access, rectification, or erasure of your personal data</li>
                    <li>Restriction or objection to processing</li>
                    <li>Data portability</li>
                    <li>Withdrawal of consent (where applicable)</li>
                  </ul>
                  <p>
                    Contact us via the Dutch Sim Pilot Discord channel to submit a request.
                  </p>
                  <p>
                    You can lodge a complaint with the Dutch Data Protection Authority (<a href="https://www.autoriteitpersoonsgegevens.nl/" className="underline text-sky-700 dark:text-sky-400">Autoriteit Persoonsgegevens</a>) if you believe your rights are infringed.
                  </p>
                </section>

                {/* 11. Changes */}
                <section>
                  <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-2">11. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. Material changes will be announced prominently on the Website. The date of the latest revision is shown below.
                  </p>
                  <p className="text-sm text-muted-foreground mt-4">
                    <b>Last updated: 30 June 2025</b>
                  </p>
                </section>
              </div>
            </Card>
            <div className="mt-10 flex justify-center">
              <Link href="/">
                <Button
                  variant="outline"
                  className="text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800 hover:bg-sky-50 dark:hover:bg-sky-900/40 transition"
                >
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}